/**
 * Stakeholder Detail Page
 * Comprehensive view of a single stakeholder with profile, relationships, and visualizations
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { auth } from '../auth';
import ChangeReasonForm from '../components/ChangeReasonForm';
import './StakeholderDetail.css';

interface StakeholderProfile {
  id: string;
  name: string;
  title: string | null;
  category: string | null;
  affiliation: string | null;
  organization: string | null;
  power_type: string | null;
  bio: string | null;
  profile_image: string | null;
  email: string | null;
  phone: string | null;
  contact_info: string | null;
  key_concerns: string | null;
  media_consumption_patterns: string | null;
  notes: string | null;
}

interface RelationshipData {
  influence_level: number | null;
  interest_level: number | null;
  relationship: string | null;
  status: string | null;
  notes: string | null;
  intent: string | null;
  strategy_mode: string | null;
  escalation_level: string | null;
}

interface StakeholderRelationship {
  id: string;
  stakeholder: {
    id: string;
    name: string;
    title: string | null;
    category: string | null;
    profile_image: string | null;
  };
  type: string;
  strength: number | null;
  direction: 'outgoing' | 'incoming';
}

interface StakeholderDetailData {
  profile: StakeholderProfile;
  relationship_data: RelationshipData;
  relationships: StakeholderRelationship[];
}

const StakeholderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [data, setData] = useState<StakeholderDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Notes editing state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Relationship editing state
  const [isEditingRelationship, setIsEditingRelationship] = useState(false);
  const [editedRelationship, setEditedRelationship] = useState({
    interest_level: 3,
    influence_level: 3,
    relationship: 'neutral' as 'supportive' | 'neutral' | 'opposed' | 'unknown',
    change_reason: ''
  });
  const [isSavingRelationship, setIsSavingRelationship] = useState(false);
  const [relationshipError, setRelationshipError] = useState<string | null>(null);

  // PDF export state
  const [exportingPdf, setExportingPdf] = useState(false);


  useEffect(() => {
    loadStakeholderDetail();
  }, [id]);

  const loadStakeholderDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getStakeholder(id!);
      setData(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load stakeholder details');
    } finally {
      setLoading(false);
    }
  };

  // Notes editing handlers
  const handleEditNotes = () => {
    setEditedNotes(data?.relationship_data.notes || '');
    setIsEditingNotes(true);
    setNotesError(null);
  };

  const handleCancelEdit = () => {
    setIsEditingNotes(false);
    setEditedNotes('');
    setNotesError(null);
  };

  const handleSaveNotes = async () => {
    if (!id) return;
    
    try {
      setIsSavingNotes(true);
      setNotesError(null);
      
      const response = await api.updateStakeholderNotes(id, editedNotes);
      
      // Update local state with new notes
      if (data) {
        setData({
          ...data,
          relationship_data: {
            ...data.relationship_data,
            notes: response.notes
          }
        });
      }
      
      setIsEditingNotes(false);
      setEditedNotes('');
    } catch (err: any) {
      setNotesError(err.message || 'Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Relationship editing handlers
  const handleEditRelationship = () => {
    if (!data) return;
    setEditedRelationship({
      interest_level: data.relationship_data.interest_level || 3,
      influence_level: data.relationship_data.influence_level || 3,
      relationship: (data.relationship_data.relationship as any) || 'neutral',
      change_reason: ''
    });
    setIsEditingRelationship(true);
    setRelationshipError(null);
  };

  const handleCancelRelationshipEdit = () => {
    setIsEditingRelationship(false);
    setEditedRelationship({
      interest_level: 3,
      influence_level: 3,
      relationship: 'neutral',
      change_reason: ''
    });
    setRelationshipError(null);
  };

  const handleSaveRelationship = async () => {
    if (!id) return;
    
    // Validate change reason
    if (!editedRelationship.change_reason.trim()) {
      setRelationshipError('Please provide a reason for this change');
      return;
    }
    
    try {
      setIsSavingRelationship(true);
      setRelationshipError(null);
      
      const response = await api.updateStakeholderRelationship(id, editedRelationship);
      
      // Update local state with new relationship data
      if (data) {
        setData({
          ...data,
          relationship_data: {
            ...data.relationship_data,
            interest_level: response.data.interest_level,
            influence_level: response.data.influence_level,
            relationship: response.data.relationship
          }
        });
      }
      
      setIsEditingRelationship(false);
    } catch (err: any) {
      setRelationshipError(err.message || 'Failed to save relationship');
    } finally {
      setIsSavingRelationship(false);
    }
  };

  // PDF Export handler
  const handleExportPDF = async () => {
    if (!id) return;
    
    try {
      setExportingPdf(true);
      
      const response = await fetch(
        `/api/portal/stakeholders/${id}/export/pdf`,
        {
          headers: {
            ...auth.getAuthHeader()
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stakeholder_${data?.profile.name?.replace(/\s+/g, '_') || 'export'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getLevelColor = (level: number | null): string => {
    if (!level) return 'level-unknown';
    if (level >= 4) return 'level-high';
    if (level >= 2) return 'level-medium';
    return 'level-low';
  };

  const getRelationshipColor = (relationship: string | null): string => {
    if (!relationship) return 'rel-unknown';
    if (relationship === 'supportive') return 'rel-supportive';
    if (relationship === 'opposed') return 'rel-opposed';
    return 'rel-neutral';
  };

  if (loading) {
    return (
      <div className="stakeholder-detail-loading">
        <div className="spinner"></div>
        <p>Loading stakeholder details...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="stakeholder-detail-error">
        <p>{error || 'Stakeholder not found'}</p>
        <button onClick={() => navigate('/stakeholders')}>
          Back to Stakeholders
        </button>
      </div>
    );
  }

  const { profile, relationship_data, relationships } = data;

  return (
    <div className="stakeholder-detail">
      {/* Back button */}
      <button className="back-button" onClick={() => navigate('/stakeholders')}>
        ← Back to Stakeholder Directory
      </button>

      {/* Header section with profile */}
      <div className="stakeholder-header">
        <div className="stakeholder-header-content">
          <div className="stakeholder-avatar-large">
            {profile.profile_image ? (
              <img src={profile.profile_image} alt={profile.name} />
            ) : (
              <div className="avatar-placeholder">{getInitials(profile.name)}</div>
            )}
          </div>

          <div className="stakeholder-header-info">
            <h1>{profile.name}</h1>
            {profile.category && (
              <span className="category-badge">{profile.category}</span>
            )}
            {profile.title && <p className="title">{profile.title}</p>}
            
            <button className="refresh-button" onClick={loadStakeholderDetail}>
              Refresh
            </button>
            <button 
              className="export-pdf-btn"
              onClick={handleExportPDF}
              disabled={exportingPdf}
              title="Export stakeholder profile to PDF"
            >
              {exportingPdf ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="stakeholder-content">
        {/* Left column - Profile information */}
        <div className="stakeholder-left">
          <div className="info-section">
            <h3>Category</h3>
            <p>{profile.category || 'Not specified'}</p>
          </div>

          <div className="info-section">
            <h3>Affiliation</h3>
            <p>{profile.affiliation || profile.organization || 'Not specified'}</p>
          </div>

          {profile.power_type && (
            <div className="info-section">
              <h3>Power Type</h3>
              <span className="power-badge">{profile.power_type}</span>
            </div>
          )}

          <div className="info-section">
            <h3>Bio</h3>
            <p>{profile.bio || 'No biography available.'}</p>
          </div>

          {profile.contact_info && (
            <div className="info-section">
              <h3>Contact Information</h3>
              <div className="contact-info">
                {profile.email && <p>📧 {profile.email}</p>}
                {profile.phone && <p>📞 {profile.phone}</p>}
                {profile.contact_info && <p>{profile.contact_info}</p>}
              </div>
            </div>
          )}

          {profile.key_concerns && (
            <div className="info-section">
              <h3>Key Concerns</h3>
              <p>{profile.key_concerns}</p>
            </div>
          )}
        </div>

        {/* Right column - Relationships and clients */}
        <div className="stakeholder-right">
          {/* Your Relationship with this Stakeholder */}
          <div className="section-card">
            <h2>Your Relationship with {profile.name}</h2>
            <div className="your-relationship">
              <div className="relationship-header">
                <h3>Your Relationship</h3>
                {!isEditingRelationship && (
                  <button 
                    className="edit-relationship-btn"
                    onClick={handleEditRelationship}
                    title="Edit relationship"
                  >
                    Edit Relationship
                  </button>
                )}
              </div>

              {isEditingRelationship ? (
                <div className="relationship-edit-form">
                  {/* Interest Level */}
                  <div className="form-group">
                    <label>Interest Level *</label>
                    <div className="radio-group">
                      {[1, 2, 3, 4, 5].map(level => (
                        <label key={level} className="radio-option">
                          <input
                            type="radio"
                            name="interest"
                            value={level}
                            checked={editedRelationship.interest_level === level}
                            onChange={() => setEditedRelationship({...editedRelationship, interest_level: level})}
                            disabled={isSavingRelationship}
                          />
                          <span>{level}</span>
                        </label>
                      ))}
                    </div>
                    <div className="level-labels">
                      <span>Very Low</span>
                      <span>Low</span>
                      <span>Medium</span>
                      <span>High</span>
                      <span>Very High</span>
                    </div>
                  </div>

                  {/* Influence Level */}
                  <div className="form-group">
                    <label>Influence Level *</label>
                    <div className="radio-group">
                      {[1, 2, 3, 4, 5].map(level => (
                        <label key={level} className="radio-option">
                          <input
                            type="radio"
                            name="influence"
                            value={level}
                            checked={editedRelationship.influence_level === level}
                            onChange={() => setEditedRelationship({...editedRelationship, influence_level: level})}
                            disabled={isSavingRelationship}
                          />
                          <span>{level}</span>
                        </label>
                      ))}
                    </div>
                    <div className="level-labels">
                      <span>Very Low</span>
                      <span>Low</span>
                      <span>Medium</span>
                      <span>High</span>
                      <span>Very High</span>
                    </div>
                  </div>

                  {/* Relationship */}
                  <div className="form-group">
                    <label>Relationship *</label>
                    <select
                      value={editedRelationship.relationship}
                      onChange={(e) => setEditedRelationship({...editedRelationship, relationship: e.target.value as any})}
                      disabled={isSavingRelationship}
                    >
                      <option value="supportive">Supportive</option>
                      <option value="neutral">Neutral</option>
                      <option value="opposed">Opposed</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>

                  {/* Change Reason */}
                  <ChangeReasonForm
                    value={editedRelationship.change_reason}
                    onChange={(reason) => setEditedRelationship({...editedRelationship, change_reason: reason})}
                  />

                  {/* Action Buttons */}
                  <div className="form-actions">
                    <button 
                      onClick={handleCancelRelationshipEdit} 
                      className="btn-cancel"
                      disabled={isSavingRelationship}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleSaveRelationship} 
                      className="btn-save"
                      disabled={isSavingRelationship}
                    >
                      {isSavingRelationship ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>

                  {relationshipError && (
                    <div className="relationship-error">
                      {relationshipError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="relationship-metrics">
                  <div className="metric-large">
                    <span className="metric-label">Influence Level</span>
                    <span className={`metric-value-large ${getLevelColor(relationship_data.influence_level)}`}>
                      {relationship_data.influence_level !== null ? relationship_data.influence_level : 'Not set'}
                    </span>
                    <p className="metric-description">How much influence this stakeholder has on your organization</p>
                  </div>
                  
                  <div className="metric-large">
                    <span className="metric-label">Interest Level</span>
                    <span className={`metric-value-large ${getLevelColor(relationship_data.interest_level)}`}>
                      {relationship_data.interest_level !== null ? relationship_data.interest_level : 'Not set'}
                    </span>
                    <p className="metric-description">How interested this stakeholder is in your organization</p>
                  </div>
                  
                  <div className="metric-large">
                    <span className="metric-label">Relationship Status</span>
                    <span className={`metric-value-large ${getRelationshipColor(relationship_data.relationship)}`}>
                      {relationship_data.relationship || 'Unknown'}
                    </span>
                    <p className="metric-description">Current stance towards your organization</p>
                  </div>
                </div>
              )}
              
              {/* Notes Section with Inline Editing */}
              <div className="relationship-notes">
                <div className="notes-header">
                  <h4>NOTES</h4>
                  {!isEditingNotes && (
                    <button 
                      className="edit-notes-btn"
                      onClick={handleEditNotes}
                      title="Edit notes"
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                {isEditingNotes ? (
                  <div className="notes-edit-container">
                    <textarea
                      className="notes-textarea"
                      value={editedNotes}
                      onChange={(e) => setEditedNotes(e.target.value)}
                      placeholder="Add notes about your relationship with this stakeholder..."
                      maxLength={10000}
                      rows={8}
                      disabled={isSavingNotes}
                    />
                    <div className="notes-edit-footer">
                      <span className="character-count">
                        {editedNotes.length} / 10,000 characters
                      </span>
                      <div className="notes-edit-actions">
                        <button
                          className="btn-cancel"
                          onClick={handleCancelEdit}
                          disabled={isSavingNotes}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn-save"
                          onClick={handleSaveNotes}
                          disabled={isSavingNotes}
                        >
                          {isSavingNotes ? 'Saving...' : 'Save Notes'}
                        </button>
                      </div>
                    </div>
                    {notesError && (
                      <div className="notes-error">
                        {notesError}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="notes-display">
                    {relationship_data.notes ? (
                      <p>{relationship_data.notes}</p>
                    ) : (
                      <p className="notes-empty">No notes yet. Click Edit to add notes about this stakeholder.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stakeholder Relationships */}
          <div className="section-card">
            <h2>Stakeholder Relationships ({relationships.length})</h2>
            
            {relationships.length === 0 ? (
              <p className="empty-message">No stakeholder relationships found.</p>
            ) : (
              <div className="relationships-list">
                {relationships.map((rel) => (
                  <div key={rel.id} className="relationship-item">
                    <div className="relationship-avatar">
                      {rel.stakeholder.profile_image ? (
                        <img src={rel.stakeholder.profile_image} alt={rel.stakeholder.name} />
                      ) : (
                        <div className="avatar-placeholder-small">
                          {getInitials(rel.stakeholder.name)}
                        </div>
                      )}
                    </div>
                    
                    <div className="relationship-info">
                      <h4>{rel.stakeholder.name}</h4>
                      <p className="relationship-title">{rel.stakeholder.title || rel.stakeholder.category}</p>
                      
                      <div className="relationship-meta">
                        <span className="relationship-type">Type: {rel.type}</span>
                        {rel.strength !== null && (
                          <span className="relationship-strength">Strength: {rel.strength}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>


        </div>
      </div>
    </div>
  );
};

export default StakeholderDetail;
